import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
  type Signal,
  type WritableSignal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router";
import { filter, map, startWith } from "rxjs";

import { AUTH_PROVIDER } from "../../../core/auth/auth-provider.token";
import { ActingOrgService } from "../../../core/services/acting-org.service";
import { AuthStateService } from "../../../core/services/auth-state.service";
import { BrandingService } from "../../../core/services/branding.service";
import { OrganizationFeaturesService } from "../../../core/services/organization-features.service";
import { switchedOffPageAt } from "../../../core/support/switched-off-page";
import { PortalDataService } from "../../../core/services/portal-data.service";
import { NavIconComponent, type NavIconName } from "../nav-icon/nav-icon.component";
import { OrgSwitcherComponent } from "../org-switcher/org-switcher.component";
import { BrandLogoComponent } from "../brand-logo/brand-logo.component";

import type { IPortalBranding } from "../../../core/models/i-portal-branding";
import type { OrganizationFeature } from "../../../core/models/organization-feature";
import type { IPortalUser } from "../../../core/models/i-portal-user";

interface INavItem {

  label: string;

  route: string;

  icon: NavIconName;

  /**
   * The rail's section heading. Items are grouped under it in order, so the nav
   * reads as three short lists rather than one column of eight links — board 8a.
   */
  section: string;

  /** Davaco employees only. */
  adminOnly?: boolean;

  /** A customer admin of the signed-in organization only. */
  customerAdminOnly?: boolean;

  /** An optional page: shown only while the organization has it switched on. */
  feature?: OrganizationFeature;
}

interface INavSection {

  label: string;

  items: INavItem[];
}

const NAV: INavItem[] = [
  { label: "Dashboard", route: "/dashboard", icon: "dashboard", section: "Overview" },
  { label: "Reports", route: "/reports", icon: "reports", section: "Program" },
  {
    label: "Documents",
    route: "/documents",
    icon: "documents",
    section: "Program",
    feature: "documents",
  },
  { label: "Activity", route: "/activity", icon: "activity", section: "Program", feature: "activity" },
  {
    label: "Members",
    route: "/members",
    icon: "organizations",
    section: "Account",
    customerAdminOnly: true,
  },
  { label: "Settings", route: "/settings", icon: "settings", section: "Account" },
  {
    label: "All Organizations",
    route: "/admin/organizations",
    icon: "organizations",
    section: "Davaco",
    adminOnly: true,
  },
  {
    label: "Auth0 Sync",
    route: "/admin/auth0-sync",
    icon: "sync",
    section: "Davaco",
    adminOnly: true,
  },
];

/** Brand rail + header + routed content — board 8a, impersonation strip from 5b. */
@Component({
  selector: "app-shell",
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NavIconComponent,
    OrgSwitcherComponent,
    BrandLogoComponent,
  ],
  templateUrl: "./app-shell.component.html",
  styleUrl: "./app-shell.component.scss",
})
export class AppShellComponent {

  private readonly provider = inject(AUTH_PROVIDER);

  private readonly brandingService = inject(BrandingService);

  private readonly authState = inject(AuthStateService);

  private readonly actingOrg = inject(ActingOrgService);

  private readonly features = inject(OrganizationFeaturesService);

  private readonly data = inject(PortalDataService);

  private readonly router = inject(Router);

  private readonly switcher = viewChild(OrgSwitcherComponent);

  /**
   * The current URL, as a signal.
   *
   * Seeded with the router's own url so the header has a title on first paint —
   * NavigationEnd has already fired by the time the shell is constructed, and
   * without the seed the title would be blank until the next navigation.
   */
  private readonly url: Signal<string> = toSignal(
    this.router.events.pipe(
      filter((event: unknown): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd): string => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  /**
   * Leave an optional page the moment it is switched off for the organization
   * in view. featureGuard only runs on navigation, so a Davaco admin sitting on
   * /activity who switches to a customer with Activity off would otherwise stay
   * on it — the link gone, the page still open. Waits for that customer's own
   * answer, as the guard does, rather than acting on the all-on default.
   */
  private readonly leaveSwitchedOffPage = effect((): void => {
    const url: string = this.url();
    if (!this.features.settled()) {
      return;
    }
    const closed: OrganizationFeature | undefined = switchedOffPageAt(
      url,
      (feature: OrganizationFeature): boolean => this.features.isOn(feature)
    );
    if (closed !== undefined) {
      untracked((): void => {
        void this.router.navigate(["/dashboard"]);
      });
    }
  });

  public readonly branding: Signal<IPortalBranding> = this.brandingService.branding;

  public readonly user: Signal<IPortalUser | null> = this.authState.user;

  public readonly isDavacoAdmin: Signal<boolean> = this.authState.isDavacoAdmin;

  public readonly collapsed: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * Nav for THIS user.
   *
   * The admin entry is filtered out rather than rendered disabled: a customer
   * has no route to it at all, and showing a dead link that names a Davaco-only
   * surface tells them something about the product they have no use for.
   */
  public readonly nav: Signal<INavSection[]> = computed((): INavSection[] => {
    const visible: INavItem[] = NAV.filter(
      (item: INavItem): boolean =>
        (item.adminOnly !== true || this.authState.isDavacoAdmin()) &&
        (item.customerAdminOnly !== true || this.authState.isCustomerAdmin()) &&
        (item.feature === undefined || this.features.isOn(item.feature))
    );

    // Grouped by first appearance rather than by a separate section list, so a
    // new nav entry only has to name its section to land in the right place.
    const sections: INavSection[] = [];
    for (const item of visible) {
      const existing: INavSection | undefined = sections.find(
        (section: INavSection): boolean => section.label === item.section
      );
      if (existing === undefined) {
        sections.push({ label: item.section, items: [item] });
      } else {
        existing.items.push(item);
      }
    }
    return sections;
  });

  /**
   * The page title in the header — board 8a puts it there rather than at the
   * top of each page, so it stays put while the content scrolls.
   *
   * Matched by longest route prefix so /admin/organizations/:slug still reads
   * as "All Organizations" rather than falling back.
   */
  public readonly pageTitle: Signal<string> = computed((): string => {
    const url: string = this.url();
    const match: INavItem | undefined = [...NAV]
      .sort((a: INavItem, b: INavItem): number => b.route.length - a.route.length)
      .find((item: INavItem): boolean => url.startsWith(item.route));
    return match?.label ?? "Customer Portal";
  });

  /**
   * The one-line strap under the page title.
   *
   * Only the dashboard gets one. A subtitle that describes the locations would
   * be wrong sitting under Settings, and a generic one that fits every page
   * says nothing.
   */
  public readonly pageSubtitle: Signal<string | null> = computed((): string | null =>
    this.url().startsWith("/dashboard")
      ? "Monitor survey progress, location activity, and data collection across all locations."
      : null
  );

  /** Only a Davaco Admin can be impersonating; a stale slug alone is not enough. */
  public readonly actingOrgSlug: Signal<string | null> = computed((): string | null =>
    this.authState.isDavacoAdmin() ? this.actingOrg.slug() : null
  );

  public readonly initials: Signal<string> = computed((): string => {
    const name: string = this.user()?.name ?? "";
    const letters: string = name
      .split(" ")
      .filter(Boolean)
      .map((part: string): string => part[0] ?? "")
      .join("");
    return letters.slice(0, 2)
.toUpperCase();
  });

  public toggleCollapsed(): void {
    this.collapsed.update((value: boolean): boolean => !value);
  }

  public openSwitcher(): void {
    this.switcher()?.toggle();
  }

  public returnToDavacoView(): void {
    this.actingOrg.returnToDavacoView();
    // The content resources key off the acting slug, so tell them to refetch
    // rather than leaving the customer's rows on screen under Davaco branding.
    this.data.reloadAll();
  }

  public async signOut(): Promise<void> {
    await this.provider.logout();
  }
}
